import { cn } from '@/lib/utils';

export type StatusVariant =
  // General
  | 'active' | 'inactive' | 'pending' | 'draft'
  // Inventory
  | 'in_stock' | 'low_stock' | 'out_of_stock' | 'on_order'
  // Shipments
  | 'booked' | 'in_transit' | 'delivered' | 'delayed' | 'cancelled'
  // Orders
  | 'submitted' | 'approved' | 'rejected' | 'processing' | 'completed'
  // Maintenance
  | 'scheduled' | 'in_progress' | 'overdue' | 'closed'
  // Partnership
  | 'terminated' | 'suspended';

const statusStyles: Record<StatusVariant, string> = {
  // Success/Active states
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  in_stock: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  closed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',

  // Warning/Pending states
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  low_stock: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  on_order: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  processing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  scheduled: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',

  // Info/Blue states
  in_transit: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  booked: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',

  // Error/Danger states
  out_of_stock: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  delayed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  terminated: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',

  // Neutral/Inactive states
  inactive: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  suspended: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
};

const statusLabels: Record<StatusVariant, string> = {
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
  draft: 'Draft',
  in_stock: 'In Stock',
  low_stock: 'Low Stock',
  out_of_stock: 'Out of Stock',
  on_order: 'On Order',
  booked: 'Booked',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  delayed: 'Delayed',
  cancelled: 'Cancelled',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  processing: 'Processing',
  completed: 'Completed',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  overdue: 'Overdue',
  closed: 'Closed',
  terminated: 'Terminated',
  suspended: 'Suspended',
};

interface StatusBadgeProps {
  status: StatusVariant | string;
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function StatusBadge({ status, label, className, size = 'md' }: StatusBadgeProps) {
  const variant = status.toLowerCase().replace(/ /g, '_') as StatusVariant;
  const style = statusStyles[variant] || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
  const displayLabel = label || statusLabels[variant] || status;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-xs',
    lg: 'px-3 py-1 text-sm',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        style,
        sizeClasses[size],
        className
      )}
    >
      {displayLabel}
    </span>
  );
}
